/**
 * FloatingNav — Floating pill navigation (replaces SidebarV2 + AppBar)
 * Desktop: centree en haut, glassmorphism, pill animee, scroll progress bar
 * Mobile: top bar (logo + brand + hamburger) + fixed bottom tab bar
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton, Drawer, useMediaQuery, useTheme } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import { colors } from '../../styles/colors';
import BrandSelector from './BrandSelector';
import NotificationBell from '../NotificationBell';

const NAV_TABS_BASE = [
  { key: 'radar', label: 'Radar', path: '/radar' },
  { key: 'comprendre', label: 'Comprendre', path: '/comprendre' },
  { key: 'agir', label: 'Agir', path: '/agir' },
  { key: 'mesurer', label: 'Mesurer', path: '/mesurer' },
];

function FloatingNav({ brands, selectedBrand, onBrandChange, hasCrisis, hasAlerts, onLogout, agency, userRole, brandPlan, isSuperadmin, hasCobrandHeader }) {
  const [logoError, setLogoError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabRefs = useRef({});
  const mobileTabRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });
  const [mobilePillStyle, setMobilePillStyle] = useState({ left: 0, width: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);

  // Build NAV_TABS: prepend Cockpit tab for admin_agence
  // Superadmin: Cockpit points to /superadmin instead of /dashboard-agence
  const NAV_TABS = React.useMemo(() => {
    const base = [...NAV_TABS_BASE];
    if (userRole === 'admin_agence') {
      base.unshift({ key: 'cockpit', label: 'Cockpit', path: isSuperadmin ? '/superadmin' : '/dashboard-agence' });
    }
    return base;
  }, [userRole, isSuperadmin]);

  // Filter tabs based on brand plan pages (if configured)
  const allowedPages = brandPlan?.pages;
  const visibleTabs = allowedPages?.length > 0
    ? NAV_TABS.filter(t => allowedPages.includes(t.key) || t.key === 'cockpit')
    : NAV_TABS;

  const showCrisisTab = hasCrisis || hasAlerts;
  const isCrisisPath = location.pathname.startsWith('/crisis');
  const isCockpitPath = location.pathname.startsWith('/dashboard-agence') || (isSuperadmin && location.pathname.startsWith('/superadmin'));
  const isSettingsPath = location.pathname.startsWith('/settings');
  const showSettingsTab = userRole === 'admin_agence';
  const accentColor = agency?.accent_color || '#B8860B';
  const activeTab = isSettingsPath && showSettingsTab
    ? 'settings'
    : isCrisisPath && showCrisisTab
      ? 'crisis'
      : isCockpitPath && userRole === 'admin_agence'
        ? 'cockpit'
        : (visibleTabs.find(t => t.key !== 'cockpit' && location.pathname.startsWith(t.path))?.key || visibleTabs[0]?.key || 'radar');

  // Update pill position when active tab changes
  const updatePill = useCallback(() => {
    const el = tabRefs.current[activeTab];
    if (el) {
      setPillStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
    }
    const mobileEl = mobileTabRefs.current[activeTab];
    if (mobileEl) {
      setMobilePillStyle({
        left: mobileEl.offsetLeft,
        width: mobileEl.offsetWidth,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    updatePill();
    window.addEventListener('resize', updatePill);
    return () => window.removeEventListener('resize', updatePill);
  }, [updatePill]);

  // Recalculate pill when tabs appear/disappear (DOM layout change)
  useEffect(() => {
    const raf = requestAnimationFrame(updatePill);
    return () => cancelAnimationFrame(raf);
  }, [showCrisisTab, showSettingsTab, visibleTabs.length, updatePill]);

  // Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Logo component (shared between mobile and desktop)
  const LogoBlock = ({ compact: isCompact }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isCompact ? '6px' : '10px',
        cursor: 'pointer',
        flexShrink: 0,
      }}
      onClick={() => navigate(isSuperadmin ? '/superadmin' : '/radar')}
    >
      {agency?.logo_url && !logoError && (
        <img
          src={agency.logo_url}
          alt={agency.name || 'Agency'}
          onError={() => setLogoError(true)}
          style={{
            width: isCompact ? 30 : 42,
            height: isCompact ? 30 : 42,
            borderRadius: '50%',
            objectFit: 'cover',
            border: `1px solid ${colors.borderLight}`,
            flexShrink: 0,
          }}
        />
      )}
      <Box
        sx={{
          width: isCompact ? 32 : 44,
          height: isCompact ? 32 : 44,
          borderRadius: isCompact ? '8px' : '10px',
          bgcolor: colors.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(27,94,64,0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: isCompact ? '2px' : '3px', height: isCompact ? 16 : 22 }}>
          <Box sx={{ width: isCompact ? 3 : 4, height: '100%', borderRadius: '2px', bgcolor: colors.white }} />
          <Box sx={{ width: isCompact ? 3 : 4, height: '72%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.8 }} />
          <Box sx={{ width: isCompact ? 3 : 4, height: '44%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.5 }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: '"Dancing Script", cursive',
          fontSize: isCompact ? '1.2rem' : '1.6rem',
          fontWeight: 700,
          color: colors.primary,
          lineHeight: 1,
        }}
      >
        Chutt...
      </Typography>
    </Box>
  );

  // For admin_agence: CobrandHeader handles Settings, Superadmin, Logout
  // FloatingNav only shows Notification bell (+ Logout for non-admin)
  const isAdmin = userRole === 'admin_agence';

  // Action buttons for drawer (mobile) or inline (desktop)
  const ActionButtons = ({ inDrawer = false }) => {
    // Build button list
    // Admin on desktop: CobrandHeader handles logout/superadmin → only bell here
    // Admin on mobile (inDrawer): CobrandHeader hidden → show logout/superadmin in drawer
    const buttons = [];
    if (!isAdmin || (isAdmin && inDrawer)) {
      if (isSuperadmin) {
        buttons.push({ icon: <AdminPanelSettingsIcon sx={{ fontSize: inDrawer ? 20 : 18 }} />, label: 'Superadmin', action: () => { navigate('/superadmin'); setMenuOpen(false); }, hoverColor: colors.gold });
      }
      if (!isAdmin) {
        buttons.push({ icon: <SettingsIcon sx={{ fontSize: inDrawer ? 20 : 18 }} />, label: 'Parametres', action: () => { navigate('/settings'); setMenuOpen(false); }, hoverColor: colors.primary });
      }
      buttons.push({ icon: <LogoutIcon sx={{ fontSize: inDrawer ? 20 : 18 }} />, label: 'Deconnexion', action: () => { onLogout(); setMenuOpen(false); }, hoverColor: colors.danger });
    }

    return (
      <Box sx={{
        display: 'flex',
        flexDirection: inDrawer ? 'column' : 'row',
        alignItems: inDrawer ? 'stretch' : 'center',
        gap: inDrawer ? 0.5 : 0.25,
        flexShrink: 0,
        ...(inDrawer && { px: 1, py: 1 }),
      }}>
        {/* Notification bell — always visible */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          px: inDrawer ? 2 : 0, py: inDrawer ? 1.5 : 0,
        }}>
          <NotificationBell brandId={selectedBrand} />
          {inDrawer && (
            <Typography sx={{ fontSize: '0.88rem', color: colors.charcoal, fontFamily: '"DM Sans", sans-serif', ml: 1.5 }}>
              Notifications
            </Typography>
          )}
        </Box>

        {buttons.map(({ icon, label, action, hoverColor }) => (
          <Box
            key={label}
            onClick={action}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: inDrawer ? 2 : 0, py: inDrawer ? 1.5 : 0,
              borderRadius: inDrawer ? '12px' : 0,
              cursor: 'pointer',
              '&:hover': { bgcolor: inDrawer ? 'rgba(27,94,64,0.06)' : 'transparent' },
            }}
          >
            {inDrawer ? (
              <>
                <Box sx={{ color: colors.textLight }}>{icon}</Box>
                <Typography sx={{ fontSize: '0.88rem', color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>{label}</Typography>
              </>
            ) : (
              <Tooltip title={label}>
                <IconButton
                  size="small"
                  aria-label={label}
                  sx={{ color: colors.textLight, '&:hover': { color: hoverColor } }}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  // ─── MOBILE LAYOUT ───
  if (isMobile) {
    return (
      <>
        {/* Scroll progress bar */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '3px',
            width: `${scrollProgress}%`,
            background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryBright})`,
            zIndex: 200,
            transition: 'width 0.1s linear',
          }}
        />

        {/* Top bar: Logo | Brand Selector | Hamburger */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 210,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${colors.borderLight}`,
          }}
        >
          <LogoBlock compact />
          <Box sx={{ flex: 1, minWidth: 0, mx: 0.5 }}>
            <BrandSelector
              brands={brands}
              selectedBrand={selectedBrand}
              onBrandChange={onBrandChange}
              compact
            />
          </Box>
          <IconButton
            size="small"
            aria-label="Menu de navigation"
            onClick={() => setMenuOpen(true)}
            sx={{ color: colors.textMed, flexShrink: 0 }}
          >
            <MenuIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        {/* Bottom tab bar */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 210,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            py: 0.75,
            pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: `1px solid ${colors.borderLight}`,
          }}
        >
          <Box
            component="nav"
            aria-label="Navigation principale"
            role="tablist"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: '3px',
              borderRadius: '100px',
              bgcolor: colors.emeraldGlass,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {/* Animated pill behind active tab */}
            <Box
              sx={{
                position: 'absolute',
                top: '3px',
                left: `${mobilePillStyle.left}px`,
                width: `${mobilePillStyle.width}px`,
                height: 'calc(100% - 6px)',
                borderRadius: '100px',
                bgcolor: activeTab === 'crisis' ? colors.bordeaux : activeTab === 'settings' ? accentColor : colors.primary,
                boxShadow: activeTab === 'crisis' ? '0 0 12px rgba(139,26,26,0.3)' : activeTab === 'settings' ? `0 0 12px ${accentColor}44` : '0 0 12px rgba(27,94,64,0.2)',
                transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
                zIndex: 0,
              }}
            />

            {visibleTabs.map(tab => (
              <Box
                key={tab.key}
                ref={el => { mobileTabRefs.current[tab.key] = el; }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === tab.key}
                onClick={() => navigate(tab.path)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(tab.path); } }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: 1,
                  textAlign: 'center',
                  py: 1.25,
                  borderRadius: '100px',
                  cursor: 'pointer',
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: activeTab === tab.key ? 600 : 500,
                  color: activeTab === tab.key ? '#fff' : colors.textMed,
                  transition: 'color 0.25s ease',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  '&:focus-visible': { outline: `2px solid ${colors.primary}`, outlineOffset: '2px' },
                }}
              >
                {tab.label}
              </Box>
            ))}

            {/* Crisis tab — bordeaux, mobile */}
            {showCrisisTab && (
              <Box
                ref={el => { mobileTabRefs.current['crisis'] = el; }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === 'crisis'}
                onClick={() => navigate('/crisis')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/crisis'); } }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: 1,
                  textAlign: 'center',
                  py: 1.25,
                  borderRadius: '100px',
                  cursor: 'pointer',
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: activeTab === 'crisis' ? '#fff' : colors.bordeaux,
                  transition: 'color 0.25s ease',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  '&:focus-visible': { outline: `2px solid ${colors.bordeaux}`, outlineOffset: '2px' },
                }}
              >
                Crise
              </Box>
            )}

            {/* Settings tab — accent_color, icon-only on mobile, admin_agence only */}
            {showSettingsTab && (
              <Box
                ref={el => { mobileTabRefs.current['settings'] = el; }}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === 'settings'}
                aria-label="Parametres"
                onClick={() => navigate('/settings')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/settings'); } }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                  width: 48,
                  py: 1.25,
                  borderRadius: '100px',
                  cursor: 'pointer',
                  color: activeTab === 'settings' ? '#fff' : accentColor,
                  transition: 'color 0.25s ease',
                  userSelect: 'none',
                  '&:focus-visible': { outline: `2px solid ${accentColor}`, outlineOffset: '2px' },
                }}
              >
                <SettingsIcon sx={{ fontSize: 18 }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Hamburger drawer */}
        <Drawer
          anchor="right"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          PaperProps={{
            sx: {
              width: 260,
              bgcolor: colors.ivory,
              borderRadius: '20px 0 0 20px',
              pt: 3,
              pb: 4,
            },
          }}
        >
          <Box sx={{ px: 2, mb: 2 }}>
            <LogoBlock compact />
          </Box>
          <Box sx={{ mx: 2, mb: 2, height: '1px', bgcolor: colors.borderLight }} />
          <ActionButtons inDrawer />
        </Drawer>
      </>
    );
  }

  // ─── DESKTOP LAYOUT ───
  return (
    <>
      {/* Scroll progress bar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '3px',
          width: `${scrollProgress}%`,
          background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryBright})`,
          zIndex: 220,
          transition: 'width 0.1s linear',
        }}
      />

      {/* Floating nav container */}
      <Box
        sx={{
          position: 'fixed',
          top: hasCobrandHeader ? 56 : 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 210,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 0.75,
          borderRadius: '100px',
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          maxWidth: '95vw',
        }}
      >
        {/* Logo — agency co-branding + Chutt... */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            px: '14px',
            pr: '20px',
            mr: '4px',
            borderRight: '1px solid rgba(27,94,64,0.12)',
            flexShrink: 0,
          }}
          onClick={() => navigate(isSuperadmin ? '/superadmin' : '/radar')}
        >
          {agency?.logo_url && !logoError && (
            <img
              src={agency.logo_url}
              alt={agency.name || 'Agency'}
              onError={() => setLogoError(true)}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `1px solid ${colors.borderLight}`,
                flexShrink: 0,
              }}
            />
          )}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(27,94,64,0.3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 20 }}>
              <Box sx={{ width: 4, height: '100%', borderRadius: '2px', bgcolor: colors.white }} />
              <Box sx={{ width: 4, height: '72%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.8 }} />
              <Box sx={{ width: 4, height: '44%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.5 }} />
            </Box>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Dancing Script", cursive',
              fontSize: '1.6rem',
              fontWeight: 700,
              color: colors.primary,
              lineHeight: 1,
            }}
          >
            Chutt...
          </Typography>
        </Box>

        {/* Tab navigation with animated pill */}
        <Box
          component="nav"
          aria-label="Navigation principale"
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            p: '3px',
            borderRadius: '100px',
            bgcolor: colors.emeraldGlass,
          }}
        >
          {/* Animated pill behind active tab */}
          <Box
            sx={{
              position: 'absolute',
              top: '3px',
              left: `${pillStyle.left}px`,
              width: `${pillStyle.width}px`,
              height: 'calc(100% - 6px)',
              borderRadius: '100px',
              bgcolor: activeTab === 'crisis' ? colors.bordeaux : activeTab === 'settings' ? accentColor : colors.primary,
              boxShadow: activeTab === 'crisis' ? '0 0 12px rgba(139,26,26,0.3)' : activeTab === 'settings' ? `0 0 12px ${accentColor}44` : '0 0 12px rgba(27,94,64,0.2)',
              transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
              zIndex: 0,
            }}
          />

          {visibleTabs.map(tab => (
            <Box
              key={tab.key}
              ref={el => { tabRefs.current[tab.key] = el; }}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === tab.key}
              onClick={() => navigate(tab.path)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(tab.path); } }}
              sx={{
                position: 'relative',
                zIndex: 1,
                px: 2,
                py: 0.75,
                borderRadius: '100px',
                cursor: 'pointer',
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.85rem',
                fontWeight: activeTab === tab.key ? 600 : 500,
                color: activeTab === tab.key ? '#fff' : colors.textMed,
                transition: 'color 0.25s ease, transform 0.2s ease',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                '&:hover': {
                  color: activeTab === tab.key ? '#fff' : colors.primary,
                  transform: activeTab === tab.key ? 'none' : 'translateY(-1px)',
                },
                '&:focus-visible': { outline: `2px solid ${colors.primary}`, outlineOffset: '2px' },
              }}
            >
              {tab.label}
            </Box>
          ))}

          {/* Crisis tab — bordeaux, appears only for brands with active crisis/alert */}
          {showCrisisTab && (
            <Box
              ref={el => { tabRefs.current['crisis'] = el; }}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === 'crisis'}
              onClick={() => navigate('/crisis')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/crisis'); } }}
              sx={{
                position: 'relative',
                zIndex: 1,
                px: 2,
                py: 0.75,
                borderRadius: '100px',
                cursor: 'pointer',
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: activeTab === 'crisis' ? '#fff' : colors.bordeaux,
                transition: 'color 0.25s ease, transform 0.2s ease',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                '&:hover': {
                  color: activeTab === 'crisis' ? '#fff' : colors.bordeaux,
                  transform: activeTab === 'crisis' ? 'none' : 'translateY(-1px)',
                },
                '&:focus-visible': { outline: `2px solid ${colors.bordeaux}`, outlineOffset: '2px' },
              }}
            >
              Crise
            </Box>
          )}

          {/* Settings tab — accent_color, admin_agence only */}
          {showSettingsTab && (
            <Box
              ref={el => { tabRefs.current['settings'] = el; }}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === 'settings'}
              onClick={() => navigate('/settings')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/settings'); } }}
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 0.75,
                borderRadius: '100px',
                cursor: 'pointer',
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.85rem',
                fontWeight: activeTab === 'settings' ? 600 : 500,
                color: activeTab === 'settings' ? '#fff' : accentColor,
                transition: 'color 0.25s ease, transform 0.2s ease',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                '&:hover': {
                  color: activeTab === 'settings' ? '#fff' : accentColor,
                  transform: activeTab === 'settings' ? 'none' : 'translateY(-1px)',
                },
                '&:focus-visible': { outline: `2px solid ${accentColor}`, outlineOffset: '2px' },
              }}
            >
              <SettingsIcon sx={{ fontSize: 16 }} />
              Parametres
            </Box>
          )}
        </Box>

        {/* Brand Selector — compact, pushed right for breathing room */}
        <Box sx={{ minWidth: 140, maxWidth: 220, flexShrink: 1, ml: 'auto' }}>
          <BrandSelector
            brands={brands}
            selectedBrand={selectedBrand}
            onBrandChange={onBrandChange}
            compact
          />
        </Box>

        {/* Action buttons */}
        <ActionButtons />
      </Box>
    </>
  );
}

export default FloatingNav;
